<template>
  <van-popup :show="show" position="bottom" :style="{ height: '70%' }" round>
    <div class="diamond-recharge-popup">
      <!-- 标题 -->
      <div class="popup-header">
        <div class="popup-header-left">
          <img src="@/assets/images/icons/go-back_grey.svg" alt="Go Back" class="back-btn" @click="handleClose" />
        </div>
        <div class="popup-title">{{ $t('product.buyDiamonds') }}</div>
        <div class="popup-header-right"></div>
      </div>

      <div class="content-wrapper">
        <template v-if="!isLoading && diamondData">
          <div>
            <div class="balance-info">
              <span class="balance-info-label">{{ $t('product.myBalance') }}</span>
              <img src="@/assets/images/icons/diamond.svg" alt="Diamond" class="diamond-icon" />
              <span class="balance-info-value">{{ userStore.memberAsset?.coin || 0 }}</span>
            </div>
            <div class="packages-grid">
              <div v-for="pkg in diamondData.products" :key="pkg.id" class="package-card" :class="{
                active: selectedProduct === pkg,
                disabled: pkg.disabled,
              }" @click="selectPackage(pkg)">
                <!-- Limit Badge -->
                <div v-if="pkg.limitInfo" class="limit-badge">
                  <div class="limit-text">
                    {{ tPlural('product.onlyLimitTimes', pkg.limitInfo.limit) }}
                  </div>
                </div>

                <!-- Discount Badge (only show if discount exists) -->
                <div v-if="pkg.desc" class="discount-badge">
                  <div class="discount-text">
                    <span class="discount-number">
                      {{ pkg.discount }}
                      <span class="discount-percent">%</span>
                    </span>
                    <span class="off-text">OFF</span>
                  </div>
                </div>

                <div class="package-top">
                  <div class="package-info">
                    <img src="@/assets/images/icons/diamond.svg" alt="Diamond" class="package-diamond" />
                    <span class="package-amount">{{ pkg.sku_count }}</span>
                  </div>
                  <template v-if="pkg.present_map">
                    <div class="present-plus">+</div>
                    <div class="present-info">
                      <template v-if="pkg.present_map.coin">
                        <img src="@/assets/images/icons/diamond.svg" alt="diamond icon" class="present-icon diamond" />
                        <div class="present-count">{{ pkg.present_map.coin }}</div>
                      </template>
                      <template v-else-if="pkg.present_map.exp_card">
                        <img src="@/assets/images/icons/exp-card_video.svg" alt="exp card icon"
                          class="present-icon exp-card" />
                        <div class="present-count">{{ pkg.present_map.exp_card }}</div>
                      </template>
                      <template v-else-if="pkg.present_map.vip">
                        <img src="@/assets/images/icons/vip-circle.svg" alt="vip icon" class="present-icon vip" />
                        <div class="present-count">{{ tPlural('common.days', pkg.present_map.vip, { days: pkg.present_map.vip }) }}</div>
                      </template>
                      <template v-else-if="pkg.present_map.message_card">
                        <img src="@/assets/images/icons/exp-card_message.svg" alt="message card icon"
                          class="present-icon message-card" />
                        <div class="present-count">{{ pkg.present_map.message_card }}</div>
                      </template>
                    </div>
                  </template>
                </div>
                <div class="package-bottom">
                  <div class="package-divider"></div>
                  <div class="package-price">
                    <span class="current-price">{{ pkg.currency_price }}</span>
                    <span v-if="pkg.currency_origin_price" class="original-price">{{ pkg.currency_origin_price }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Payment Section -->
            <div v-if="availablePaymentMethods?.length" class="payment-section">
              <p class="payment-title">{{ $t('product.selectPaymentMethod') }}</p>
              <div class="payment-methods">
                <div v-for="(method, index) in availablePaymentMethods" :key="index" class="payment-option"
                  :class="{ active: selectedPayment === method }" @click="selectPayment(method)">
                  <img v-if="method.image" :src="method.image" :alt="method.name" class="payment-logo" />
                  <div class="payment-info">
                    <span class="payment-name">{{ method.name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Purchase Button -->
          <div class="purchase-section">
            <button class="purchase-button" :disabled="isPaying" @click="handlePurchase">
              {{ isPaying ? $t('product.processing') : $t('product.purchase') }}
            </button>
          </div>
        </template>
        <div v-else class="content-loading">
          <van-loading />
        </div>
      </div>
    </div>
  </van-popup>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { getDiamondList, createPayment } from '@/api/product'
import { showToast, showConfirmDialog } from 'vant'
import { SCENE_TYPES } from '@/composables/usePaymentRedirect'
import useSWRV from 'swrv'
import { useUserStore } from '@/stores/user'
import { track } from '@/utils/tracker'
import { parseLimitDesc } from '@/utils'
import { tPlural } from '@/locales'
import { NUMBER_REGEXP } from '@/common/constants/regexp'

const { t } = useI18n()

const userStore = useUserStore()

const props = defineProps({
  /**
   * 是否显示弹窗
   */
  show: {
    type: Boolean,
    default: false,
  },
  action: {
    type: String,
    default: '',
  },
  /**
   * 进入来源 (用于埋点)
   */
  from: {
    type: String,
    default: '',
  }
})

// 上报商品购买点击埋点
// 根据 from 参数映射 pay_entry_point
const payEntryPointMap = {
  'call_low_balance': 'h5_video_call_popup',
  'chat_no_balance': 'h5_video_call_popup',
}

const emit = defineEmits(['update:show', 'purchase', 'close'])

const isPaying = ref(false)

// Selected states
const selectedProduct = ref(null)
const selectedPayment = ref(null)

// 使用 swrv 获取钻石商品列表
const { data: diamondData, isLoading, mutate } = useSWRV(
  () => `diamond-products-${props.action}`,
  async () => {
    const data = await getDiamondList({ action: props.action }, { hideLoading: true })
    if (!data?.products) return data
    return {
      ...data,
      products: data.products.map(product => {
        const limitInfo = parseLimitDesc(product.limit_desc)
        const newProduct = {
          ...product,
          discount: product.desc?.match(NUMBER_REGEXP)?.[0],
          limitInfo,
        }
        if (limitInfo) {
          newProduct.disabled = limitInfo.limit <= limitInfo.purchased
        }
        return newProduct
      })
    }
  },
  {
    revalidateOnFocus: false,
  }
)

watch(() => props.action, () => {
  mutate()
})

watch(() => props.show, (newVal) => {
  if (newVal) {
    userStore.fetchMemberAsset({ hideLoading: true })

    // 上报商店页面曝光埋点
    track('mall_page_view_dynamic', {
      store_type: 'diamond_store',
      enter_source: props.from,
    })
  }
}, { immediate: true })

// 监听数据加载完成，自动选中第一个未达限购的商品
watch(diamondData, (data) => {
  if (data?.products?.length) {
    const firstAvailable = data.products.find(pkg => !pkg.disabled)
    // 只选择未达限购的商品，如果所有商品都达到限购则不选择
    if (firstAvailable) {
      selectedProduct.value = firstAvailable
      if (firstAvailable?.pay_funcs?.length) {
        selectedPayment.value = firstAvailable.pay_funcs[0]
      } else {
        selectedPayment.value = null
      }
      return
    }
  }
  selectedProduct.value = null
  selectedPayment.value = null
}, { immediate: true })

// Get available payment methods - 从当前选中商品的pay_funcs获取
const availablePaymentMethods = computed(() => selectedProduct.value?.pay_funcs || [])

// Select package（已达限购的不可选）
const selectPackage = (product) => {
  if (product.disabled) return
  selectedProduct.value = product
  // Reset payment selection when switching packages
  if (product.pay_funcs?.length) {
    selectedPayment.value = product.pay_funcs[0]
  } else {
    selectedPayment.value = null
  }
}

// Select payment method
const selectPayment = (payment) => {
  selectedPayment.value = payment
}

const openPaymentWindow = (url) => {
  const isOpen = window.open(url, '_blank', 'width=800,height=600,left=200,top=100,toolbar=no,menubar=no,scrollbars=yes,resizable=yes')
  return isOpen
}
// Handle purchase
const handlePurchase = async () => {
  // Validation
  if (!selectedProduct.value) {
    showToast(t('product.pleaseSelectPackage'))
    return
  }

  if (!selectedPayment.value || !selectedPayment.value) {
    showToast(t('product.pleaseSelectPayment'))
    return
  }

  if (isPaying.value) return

  isPaying.value = true

  const payEntryPoint = payEntryPointMap[props.from] || 'h5_video_call_popup'

  track('payment_initiate_mall_page', {
    pay_entry_point: payEntryPoint,
    product_id: selectedProduct.value.id,
    product_type: 'diamond',
    amount: selectedProduct.value.payment_dollar,
    pay_environment: 'out_station_web',
  })

  emit('purchase', {
    product: selectedProduct.value,
    payment: selectedPayment.value,
  })

  try {
    const response = await createPayment({
      config_method: selectedPayment.value.pay_method,
      config_func: selectedPayment.value.pay_func,
      product_id: selectedProduct.value.id,
      pay_price: selectedProduct.value.payment_dollar, // 使用商品的支付金额
      scene: SCENE_TYPES.DIAMOND_PAGE, // 场景标识,用于支付回调后重定向
    })

    if (response?.redirect_url) {
      const isOpen = openPaymentWindow(response.redirect_url)
      if (!isOpen) {
        showConfirmDialog({
          title: t('product.paymentWindowOpenFailed.title'),
          message: t('product.paymentWindowOpenFailed.message'),
          confirmButtonText: t('product.paymentWindowOpenFailed.continue'),
          cancelButtonText: t('common.cancel'),
          onConfirm: () => openPaymentWindow(response.redirect_url),
        })
      }
    } else {
      throw new Error(t('product.invalidPaymentResponse'))
    }
  } catch (err) {
    const errorMessage = err.message || t('product.paymentFailed')
    showToast(errorMessage)
  } finally {
    isPaying.value = false
  }
}

const handleClose = () => {
  emit('update:show', false)
}
</script>

<style scoped lang="less">
@header-height: 50px;

.diamond-recharge-popup {
  height: 100%;
  background: #FFF;

  // 头部
  .popup-header {
    display: flex;
    width: 100%;
    height: @header-height;
    box-sizing: border-box;
    padding: 0 16px;
    justify-content: space-between;
    align-items: center;

    .popup-header-left,
    .popup-header-right {
      width: 50px;

      .back-btn {
        width: 24px;
        height: 24px;
        cursor: pointer;
      }
    }

    .popup-title {
      width: 150px;
      color: #2B2D42;
      text-align: center;
      font-family: Roboto;
      font-size: 18px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
      text-transform: capitalize;
      .ellipsis();
    }
  }

  // Content Wrapper
  .content-wrapper {
    height: calc(100% - @header-height);
    overflow-y: auto;
    background: #ffffff;
    border-radius: 16px;
    padding: 16px 12px;
    padding-top: 8px;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    .hide-scrollbar();

    .balance-info {
      height: 30px;
      display: flex;
      align-items: center;
      gap: 4px;
      margin-bottom: 12px;

      &-label {
        color: #74758A;
        font-family: Roboto;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        text-transform: capitalize;
      }

      .diamond-icon {
        width: 16px;
        height: 16px;
      }

      &-value {
        color: #494B64;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
        text-transform: capitalize;
      }
    }

    // Packages Grid
    .packages-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 6px;
      margin-bottom: 16px;

      .package-card {
        background: #f7f7f7;
        border: 2px solid #e1e1e1;
        border-radius: 16px;
        padding: 16px 0 8px;
        text-align: center;
        cursor: pointer;
        transition: all 0.3s;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: 10px;
        align-items: center;
        position: relative;

        &.active {
          border: 2px solid #e9c163;
        }

        &.disabled {
          .disabled();
        }

        .limit-badge {
          width: 76px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: absolute;
          left: -5px;
          top: -5px;
          transform-origin: left top;
          transform: scale(.8);
          padding: 0 7px 4px 4px;
          background-image: url('./assets/limit-diamond-bg.svg');
          background-size: 100% 100%;
          background-repeat: no-repeat;

          .limit-text {
            color: #FFF;
            text-align: center;
            font-family: "PingFang SC";
            font-size: 9px;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
            .ellipsis();
          }
        }

        .discount-badge {
          position: absolute;
          top: -4px;
          right: -4px;

          .discount-text {
            position: absolute;
            top: 53%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(16deg);
            display: flex;
            flex-direction: column;
            align-items: center;
            color: #ffffff;
            line-height: 1;

            .discount-number {
              display: flex;
              align-items: flex-end;
              font-size: 12px;
              font-weight: bold;

              .percent {
                font-size: 8px;
              }
            }

            .off-text {
              font-size: 8px;
            }
          }
        }

        .package-top {
          height: 60px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 3px;

          .package-info {
            height: 16px;
            display: flex;
            align-items: center;
            gap: 4px;
            justify-content: center;

            .package-diamond {
              width: 20px;
              height: 20px;
            }

            .package-amount {
              font-weight: bold;
              font-size: 16px;
              color: #29244c;
            }
          }

          .present-plus {
            width: fit-content;
            height: 19px;
            padding-bottom: 3px;
            box-sizing: border-box;
            color: #F87F03;
            font-family: Roboto;
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: 16px;
          }

          .present-info {
            height: 20px;
            display: flex;
            padding: 0 6px;
            justify-content: center;
            align-items: center;
            gap: 4px;
            align-self: stretch;
            border-radius: 16px;
            background: #FFF;

            .present-icon {
              width: 12px;
              height: 12px;

              &.message-card {
                width: 16px;
                height: 16px;
              }

              &.exp-card {
                width: 17px;
                height: 17px;
              }
            }

            .present-count {
              color: #F87F03;
              font-size: 12px;
              font-style: normal;
              font-weight: 500;
              line-height: normal;
            }
          }
        }

        .package-bottom {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;

          .package-divider {
            width: 85%;
            height: 1px;
            background-image: url('./assets/divider.svg');
            background-repeat: no-repeat;
            background-size: contain;
          }

          .package-price {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 4px;

            .current-price {
              font-weight: 500;
              font-size: 12px;
              color: #29244c;
            }

            .original-price {
              font-size: 10px;
              color: #cccdd8;
              text-decoration: line-through;
            }
          }
        }
      }
    }

    // Payment Section
    .payment-section {
      margin-bottom: 16px;

      .payment-title {
        font-family: 'PingFang SC', sans-serif;
        font-size: 12px;
        color: #74758a;
        margin-bottom: 12px;
      }

      .payment-methods {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;

        .payment-option {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          background: #f7f7f7;
          border: 1px solid #e1e1e1;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s;
          height: 43px;
          box-sizing: border-box;

          &.active {
            border: 2px solid #e9c163;
          }

          .payment-logo {
            width: 28px;
            height: 28px;
            object-fit: contain;
          }

          .payment-info {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;

            .payment-name {
              font-family: 'PingFang SC', sans-serif;
              font-size: 14px;
              color: #615d70;
            }

            .estimate-amount {
              font-size: 10px;
              color: #8b8b8b;
              font-weight: 400;
            }
          }
        }
      }
    }

    // Purchase Section
    .purchase-section {
      width: 304px;
      height: 44px;
      position: sticky;
      bottom: 0;
      box-sizing: border-box;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      z-index: 10;
      margin: 0 auto;
      flex-shrink: 0;

      .purchase-button {
        width: 100%;
        height: 100%;
        background: linear-gradient(173.2deg, #c764ff 3.0158%, #7f4aff 96.077%);
        border-radius: 54px;
        border: none;
        font-family: 'Roboto', sans-serif;
        font-weight: 900;
        font-size: 16px;
        color: #ffffff;
        cursor: pointer;
        transition: opacity 0.3s;

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        &:active:not(:disabled) {
          opacity: 0.8;
        }
      }
    }

    .content-loading {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}
</style>
